import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldErrorDto, useSubmitCancellationNoticeMutation } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { DoffinNoAnswerAlert } from "./DoffinNoAnswerAlert";
import { DoffinRefusalAlert, doffinRefusal } from "./DoffinRefusalAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "./doffinNoAnswer";

interface Props {
  eventId: string;
  reason: string;
  isOpen: boolean;
  onClose: () => void;
}

export const CancellationNoticeModal = ({ eventId, reason, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const [refusal, setRefusal] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const [submit, { isLoading }] = useSubmitCancellationNoticeMutation();

  const send = async () => {
    setRefusal(null);
    setNoAnswer(null);
    try {
      await submit({ sourcingEventId: eventId }).unwrap();
      toast({ title: t("The change notice is sent to Doffin"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      const breaches = doffinRefusal(e);
      const unanswered = doffinNoAnswer(e);
      if (breaches) setRefusal(breaches);
      else if (unanswered) setNoAnswer(unanswered);
      else trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">{t("Announce the cancellation on Doffin")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing="5">
            <Text>
              {t("A change notice tells Doffin that the procurement is cancelled, with your reason as its description.")}
            </Text>
            <Box>
              <Text fontWeight="semibold">{t("Reason for cancellation")}</Text>
              <Text whiteSpace="pre-wrap">{reason}</Text>
            </Box>
            {refusal && <DoffinRefusalAlert breaches={refusal} />}
            {noAnswer && <DoffinNoAnswerAlert noAnswer={noAnswer} />}
          </Stack>
          <Flex justify="end" pt="6">
            <Button
              colorScheme="teal"
              isLoading={isLoading}
              isDisabled={noAnswer === "whileSending"}
              onClick={send}
            >
              {t("Send the change notice")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
